import React from 'react'
import { Task } from '../../lib/types/database.types'
import {
  TASK_PRIORITY_COLORS,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
} from '../../lib/config/project-config'
import { formatDate, formatRelativeTime } from '../../utils/formatters'
import { getTaskCurrentStatusDueDate } from '../../utils/statusDates'
import { RichTextContent } from '../common/RichTextContent'

type TaskInfoPanelProps = {
  task: Task
  assigneeLabel?: string
}

const badgeClassName = 'inline-flex rounded-full border px-2.5 py-1 text-xs font-medium'
const detailLabelClassName = 'text-xs uppercase tracking-wide text-gray-500'
const detailValueClassName = 'text-sm text-gray-800'

export const TaskInfoPanel: React.FC<TaskInfoPanelProps> = ({ task, assigneeLabel }) => {
  const statusColor = TASK_STATUS_COLORS[task.status as keyof typeof TASK_STATUS_COLORS] || '#6b7280'
  const devPriorityColor = TASK_PRIORITY_COLORS[task.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280'
  const salesPriorityColor = TASK_PRIORITY_COLORS[task.client_priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280'
  const dueDate = getTaskCurrentStatusDueDate(task)

  return (
    <section className="rounded-xl border border-stone-300 bg-stone-50 p-5">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">Information</h3>
        <p className="text-sm text-gray-500">Request overview</p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <p className={detailLabelClassName}>Task ID</p>
          <p className={detailValueClassName}>{task.unique_task_id}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Title</p>
          <p className="text-base font-medium text-gray-900">{task.title}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Status</p>
          <span className={badgeClassName} style={{ borderColor: statusColor, color: statusColor }}>
            {TASK_STATUS_LABELS[task.status as keyof typeof TASK_STATUS_LABELS] || task.status}
          </span>
        </div>
        <div>
          <p className={detailLabelClassName}>Dev Priority</p>
          <span className={badgeClassName} style={{ borderColor: devPriorityColor, color: devPriorityColor }}>
            {TASK_PRIORITY_LABELS[task.priority as keyof typeof TASK_PRIORITY_LABELS] || task.priority}
          </span>
        </div>
        <div>
          <p className={detailLabelClassName}>Sales Priority</p>
          <span className={badgeClassName} style={{ borderColor: salesPriorityColor, color: salesPriorityColor }}>
            {TASK_PRIORITY_LABELS[task.client_priority as keyof typeof TASK_PRIORITY_LABELS] || task.client_priority}
          </span>
        </div>
        <div>
          <p className={detailLabelClassName}>Weight</p>
          <p className={detailValueClassName}>{task.weight ?? 'Not set'}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Type</p>
          <p className={detailValueClassName}>{TASK_TYPE_LABELS[task.type as keyof typeof TASK_TYPE_LABELS] || task.type}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Assigned To</p>
          <p className={detailValueClassName}>{assigneeLabel || 'Unassigned'}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Requested By</p>
          <p className={detailValueClassName}>{task.requested_by || 'Not provided'}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Related App</p>
          <p className={detailValueClassName}>{task.related_app || 'None'}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Current Status Due Date</p>
          <p className={detailValueClassName}>{dueDate ? formatDate(dueDate) : 'Not set'}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Overall Due Date</p>
          <p className={detailValueClassName}>{task.overall_due_date ? formatDate(task.overall_due_date) : 'Not set'}</p>
        </div>
        <div>
          <p className={detailLabelClassName}>Support Ticket No</p>
          <p className={detailValueClassName}>{task.support_ticket_no || 'Not set'}</p>
        </div>
        <div className="md:col-span-2">
          <p className={detailLabelClassName}>Updated</p>
          <p className={detailValueClassName}>{formatRelativeTime(task.updated_at)}</p>
        </div>
        <div className="md:col-span-2">
          <p className={detailLabelClassName}>Description</p>
          <div className="mt-2 rounded-lg border border-stone-200 bg-white/80 p-4 text-sm text-gray-700">
            <RichTextContent html={task.description || ''} emptyText="No description provided." />
          </div>
        </div>
      </div>
    </section>
  )
}
